import { cloneElement, PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';

import SuggestLayer from 'bloko/common/constants/layersCssClasses';
import debounce from 'bloko/common/debounce';

import SuggestPicker from './SuggestPicker';
import Defaults from './defaults';
import { normalizeText, isObjectsEqual } from './utils';

import './suggest.less';

/**
 * Саджест позволяет добавить к любому текстовому полю формы возможность выбора вариантов,
 * чтобы не пришлось вводить всё слово или фразу целиком.
 */
class Suggest extends PureComponent {
    static propTypes = {
        /**
         * Включает функцию автовыбора. Если найдено всего одно совпадение и
         * оно полностью совпадает с текстом, введенным в поле,
         * происходит автоматический выбор этого пункта.
         */
        autoSelect: PropTypes.bool,
        /** Выбор первого элемента из найденных при потере фокуса у поля */
        selectOnBlur: PropTypes.bool,
        /**
         * Выбор элемента с полностью совпадающим текстом (при наличии такого, без учета пробелов и регистра)
         * при потере фокуса у поля
         */
        selectExactMatchOnBlur: PropTypes.bool,
        /**
         * Функция, возвращающая промис в `resolve` которого передается список элементов для саджеста, при вызове
         * функции первым параметром передается содержимое поля с применением `trim` и `toUpperCase`
         */
        dataProvider: PropTypes.func.isRequired,
        /** Инпут, на который будет навешен функционал саджеста */
        children: PropTypes.element.isRequired,
        /** Максимальное количество отображаемых в дропдауне результатов */
        limit: PropTypes.number,
        /** Функция рендера блока со списком элементов. См. `renderItems` в `SuggestPicker`. */
        renderItems: PropTypes.func,
        /** Функция для SuggestPicker, которая возвращает содержимое переданного в нее item */
        itemContent: PropTypes.func,
        /** Функция для SuggestPicker, которая возвращает key для переданного в нее item */
        itemKey: PropTypes.func,
        /**
         * Функция, которая должна вернуть текст переданного в нее item, по умолчанию возвращает
         * item[Defaults.FIELD]
         */
        itemText: PropTypes.func,
        /**
         * Коллбэк, вызываемый при попытке изменения выбранного item, первым параметром передается сам объект item,
         * вторым параметром передаётся поисковая строка
         */
        onChange: PropTypes.func,
        /** Минимальное количество символов для показа SuggestPicker */
        suggestStartInputLength: PropTypes.number,
        /**
         * Задержка обновления списка при изменении содержимого поля в миллисекундах (лимитирование запросов
         * к dataProvider)
         */
        debounceDelay: PropTypes.number,
        /** После отображения саджеста автоматически выделять первый пункт. По enter будет выбран этот вариант */
        autoHighlightFirstSuggest: PropTypes.bool,
        /** Положение списка по оси Z (устанавливает z-index) [Возможные варианты](#suggest-layers) */
        layer: PropTypes.oneOf(Object.values(SuggestLayer)),
        /**
         * Значение саджеста, в случае, если передано - саджест будет контролируемым, как в
         * [этом примере](#controlled-example)
         */
        value: PropTypes.object,
        /**
         * Значение по умолчанию для саджеста, изменение этого пропа на уже отрисованном компоненте не производят
         * никакого эффекта
         */
        defaultValue: PropTypes.object,
        /**
         * Используется для установки первоначального children.props.value (например инпута), изменения этого
         * пропа на уже отрисованном компоненте не производят никакого эффекта. Будет проигнорирован, если передан
         * defaultValue
         */
        defaultSearch: PropTypes.string,
        /**
         * Может ли быть показан picker саджеста
         */
        allowPicker: PropTypes.bool,
        /**
         * Принудительно ищет совпадения с введенным текстом при фокусе на инпут садежста, даже при наличии выбранного элемента.
         * Работает при соответствии количества символов свойству suggestStartInputLength.
         */
        searchOnFocus: PropTypes.bool,
        /** Указывает на строку с компонентом в исходном коде в режиме разработки. Генерируется babel-plugin-react-source */
        source: PropTypes.string,
    };

    static defaultProps = {
        autoSelect: Defaults.AUTOSELECT,
        allowPicker: Defaults.ALLOW_PICKER,
        selectOnBlur: Defaults.SELECTION_ON_BLUR,
        selectExactMatchOnBlur: Defaults.SELECT_EXACT_MATCH_ON_BLUR,
        limit: Defaults.LIMIT,
        itemText: (item) => item[Defaults.FIELD],
        suggestStartInputLength: Defaults.SUGGEST_START_INPUT_LENGTH,
        debounceDelay: Defaults.THROTTLE,
        searchOnFocus: Defaults.SEARCH_ON_FOCUS,
    };

    static getDerivedStateFromProps(props, state) {
        const isControlled = typeof props.value !== 'undefined';

        if (!isControlled) {
            return null;
        }

        if (!props.value) {
            return { value: null };
        }
        const newValue = { ...props.value };

        return Suggest.getStateByValue(
            newValue,
            props.itemText,
            !(state.value && isObjectsEqual(state.value, newValue))
        );
    }

    static getStateByValue(value, itemText, hidePicker = true) {
        if (!value) {
            return { value: null };
        }

        const searchValue = itemText(value);
        const newState = {
            value,
            search: searchValue,
            preparedSearchValue: normalizeText(searchValue),
        };

        if (hidePicker) {
            newState.pickerEnabled = false;
        }

        return newState;
    }

    defaultSearchValue = this.getDefaultSearchValue();

    state = {
        pickerItems: [],
        pickerEnabled: false,
        value: this.props.defaultValue ? this.props.defaultValue : null,
        search: this.defaultSearchValue,
        preparedSearchValue: normalizeText(this.defaultSearchValue),
    };

    componentWillUnmount() {
        this.updatePickerDebounced.cancel();
    }

    componentDidUpdate = (prevProps) => {
        if (this.props.value !== prevProps.value) {
            this.setState(Suggest.getStateByValue(this.props.value, this.props.itemText));
        }
    };

    getDefaultSearchValue() {
        const { defaultValue, defaultSearch, itemText } = this.props;
        if (defaultValue) {
            return itemText(defaultValue);
        }
        return defaultSearch || '';
    }

    isControlled = () => {
        return typeof this.props.value !== 'undefined';
    };

    inputRef = (element) => {
        const { passedInputRef } = this;
        if (passedInputRef && passedInputRef.hasOwnProperty('current')) {
            passedInputRef.current = element;
        } else if (typeof passedInputRef === 'function') {
            passedInputRef(element);
        }
        this.inputRefElement = element;
    };

    renderClonedInputElement = () => {
        const input = this.props.children;
        this.passedInputRef = input.ref;
        this.passedInputOnChange = input.props.onChange;
        this.passedInputOnBlur = input.props.onBlur;
        this.passedInputOnFocus = input.props.onFocus;
        const reassignedInputProps = {
            onChange: this.handleInputChange,
            onBlur: this.handleInputBlur,
            onFocus: this.handleInputFocus,
            ref: this.inputRef,
            source: this.props.source,
            autoComplete: 'off',
            'aria-autocomplete': 'list',
        };

        return cloneElement(input, { ...reassignedInputProps, value: this.state.search });
    };

    updatePicker = (newPreparedSearchValue) => {
        const { suggestStartInputLength, dataProvider, itemText, autoSelect, limit, allowPicker } = this.props;
        this.setState({ preparedSearchValue: newPreparedSearchValue });

        if (!allowPicker) {
            return;
        }

        if (newPreparedSearchValue.length < suggestStartInputLength) {
            this.setState({
                pickerItems: [],
                pickerEnabled: false,
            });
            return;
        }

        dataProvider(newPreparedSearchValue)
            .then((data) => {
                const { items } = data;

                // Значение инпута было изменено, результаты не актуальны
                if (newPreparedSearchValue !== this.state.preparedSearchValue) {
                    return;
                }

                this.setState({
                    pickerEnabled: true,
                    pickerItems: items.slice(0, limit),
                });

                if (autoSelect && items.length === 1 && normalizeText(itemText(items[0])) === newPreparedSearchValue) {
                    this.handleItemSelect(items[0]);
                }
            })
            .catch(() => {
                if (newPreparedSearchValue !== this.state.preparedSearchValue) {
                    return;
                }
                this.setState({
                    pickerItems: [],
                    pickerEnabled: false,
                });
            });
    };

    updatePickerDebounced = debounce(this.updatePicker, this.props.debounceDelay);

    handleInputFocus = (...rest) => {
        if (this.passedInputOnFocus) {
            this.passedInputOnFocus(...rest);
        }

        const { itemText, searchOnFocus } = this.props;
        const { search, value } = this.state;

        if (value && itemText(value) && !searchOnFocus) {
            return;
        }

        const newPreparedSearchValue = normalizeText(search);

        this.updatePickerDebounced(newPreparedSearchValue);
    };

    handleInputChange = (eventOrValue, ...rest) => {
        if (this.passedInputOnChange) {
            this.passedInputOnChange(eventOrValue, ...rest);
        }
        // TODO: Удалить вариант с event после перехода на новый инпут: PORTFOLIO-17369
        // Нужен для совместимости саджеста со старым инпутом.
        const searchValue = eventOrValue.target ? eventOrValue.target.value : eventOrValue;
        const newPreparedSearchValue = normalizeText(searchValue);

        this.setState({ search: searchValue });

        if (newPreparedSearchValue === this.state.preparedSearchValue) {
            return;
        }

        if (this.state.value) {
            this.handleItemSelect(null, searchValue);
        }

        this.updatePickerDebounced(newPreparedSearchValue);
    };

    handleInputBlur = (...rest) => {
        if (this.passedInputOnBlur) {
            this.passedInputOnBlur(...rest);
        }
        const { itemText, selectOnBlur, selectExactMatchOnBlur, allowPicker } = this.props;
        const { pickerItems, search, pickerEnabled } = this.state;

        this.setState({ pickerEnabled: false });

        if (pickerEnabled && allowPicker) {
            let selectedItem;

            if (selectOnBlur) {
                selectedItem = pickerItems[0];
            } else if (selectExactMatchOnBlur) {
                const normalizedInputValue = normalizeText(search);
                selectedItem =
                    pickerItems.find((item) => normalizeText(itemText(item)) === normalizedInputValue) || null;
            } else {
                return;
            }

            this.handleItemSelect(selectedItem);
        }
    };

    handleItemSelect = (item, searchValue) => {
        const itemClone = item ? Object.assign({}, item) : null;
        const { onChange } = this.props;

        if (onChange) {
            onChange(itemClone, searchValue);
        }

        if (!this.isControlled()) {
            this.selectItem(itemClone);
        }
    };

    handlePickerHide = () => {
        this.setState({ pickerEnabled: false });
    };

    handlePickerShow = () => {
        this.setState({ pickerEnabled: true });
    };

    selectItem(item = null) {
        this.setState(Suggest.getStateByValue(item, this.props.itemText));
    }

    renderPicker() {
        const { itemContent, itemKey, renderItems, autoHighlightFirstSuggest, layer, allowPicker } = this.props;
        const { pickerEnabled, pickerItems } = this.state;

        if (!this.inputRefElement) {
            return null;
        }

        return (
            <SuggestPicker
                element={this.inputRefElement}
                items={pickerItems}
                itemContent={itemContent}
                itemKey={itemKey}
                renderItems={renderItems}
                onItemSelect={this.handleItemSelect}
                autoHighlightFirstSuggest={autoHighlightFirstSuggest}
                onHide={this.handlePickerHide}
                onShow={this.handlePickerShow}
                layer={layer}
                enabled={pickerEnabled && allowPicker}
            />
        );
    }

    render() {
        return (
            <Fragment>
                {this.renderClonedInputElement()}
                {this.renderPicker()}
            </Fragment>
        );
    }
}

export default Suggest;
export { SuggestLayer };
